import { FC, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';

export type SingleSelectOption = {
	value: string;
	label: string;
};

type Props = {
	value: string | null | undefined;
	onChange: (value: string | null) => void;
	options: SingleSelectOption[];
	placeholder?: string;
	allOptionLabel?: string;
	disabled?: boolean;
	modal?: boolean;
	triggerClassName?: string;
	contentClassName?: string;
};

const ESingleSelectPicker: FC<Props> = ({
	value,
	onChange,
	options,
	placeholder,
	allOptionLabel,
	disabled = false,
	modal = true,
	triggerClassName,
	contentClassName,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const displayLabel = useMemo(() => {
		if (value === null || value === undefined) {
			return allOptionLabel ?? placeholder ?? '';
		}
		const selected = options.find((option) => option.value === value);
		return selected?.label ?? allOptionLabel ?? placeholder ?? '';
	}, [allOptionLabel, options, placeholder, value]);

	const handleSelect = (nextValue: string | null) => {
		onChange(nextValue);
		setIsOpen(false);
	};

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={modal}>
			<DropdownMenuTrigger asChild>
				<Button
					variant='outline'
					disabled={disabled}
					className={cn(
						'h-10 w-full min-w-0 justify-between rounded-md px-3 py-2 text-sm font-normal',
						isOpen && 'border-primary-color-1000',
						triggerClassName,
					)}>
					<span className='truncate text-popover-1000'>
						{displayLabel}
					</span>
					<div
						className={cn(
							'ml-2 shrink-0 transition-transform duration-50 ease-in-out',
							isOpen ? '-rotate-180' : 'rotate-0',
						)}>
						<ArrowDownIcon />
					</div>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className={cn(
					'w-(--radix-dropdown-menu-trigger-width) max-h-56 p-0 rounded-md border border-popover-100 bg-popover-10 overflow-y-auto',
					contentClassName,
				)}>
				{allOptionLabel !== undefined && (
					<DropdownMenuCheckboxItem
						className='px-3 py-2.5'
						checked={value === null || value === undefined}
						onSelect={(e) => e.preventDefault()}
						onCheckedChange={() => handleSelect(null)}>
						{allOptionLabel}
					</DropdownMenuCheckboxItem>
				)}
				{options.map((option) => (
					<DropdownMenuCheckboxItem
						key={option.value}
						className='px-3 py-2.5'
						checked={value === option.value}
						onSelect={(e) => e.preventDefault()}
						onCheckedChange={() => handleSelect(option.value)}>
						{option.label}
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ESingleSelectPicker;
